import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useLocation, useHistory } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import Link from './link/Link';
import Logo from './logo/Logo';
import styles from './header.module.scss';
import UserProfile from './user-profile/UserProfile';
import ArrowDownIcon from '../../assets/icons/chevron_down.svg?react';
import BellIcon from '@src/assets/icons/bell.svg?react';
import ArrowRightIcon from '@src/assets/icons/arrow_right.svg?react';
import { useLoginMutation } from '../../hooks/useLoginMutation';
import { useAuthToken } from '@src/hooks/useAuthToken';
import { useHeaUser } from '@src/hooks';
import DropDown from './dropdown/Dropdown';
import CompletedLessonsModal from '../unit-progress-modal/CompletedLessonsModal';
import { HeaUnit, LessonToConfirmFragment, StudyPlan } from '@src/api/graphql-codegen';
import Spinner from '../spinner/Spinner';

type HeaderProps = {
  isParentApp?: boolean;
  maintain?: boolean;
  isProfile?: boolean;
  showStudyPlans?: boolean;
  handleShowStudyPlans?: () => void;
  existStudyPlan?: boolean;
  isHidden?: boolean;
  studyPlans?: StudyPlan[];
  heaUnits?: HeaUnit[];
  refresh?: boolean;
  confirmLessons?: LessonToConfirmFragment[];
  refetch?: () => void;
  loading?: boolean;
};

const menuText = {
  help: "Help",
  helpCentre: "Help Centre",
  profile: "Profile",
  logout: "Logout",
}
const mobileWidth = 600;
function HeaHeader(props: HeaderProps) {
  const {isParentApp, isHidden=false, studyPlans, heaUnits, confirmLessons, loading, refetch} = props;
  const { pathname, search } = useLocation();
  const isParentAppCheck = isParentApp || pathname.startsWith('/parent');
  const [reviewLessonId, studyPlanId] = useMemo(() => {
    if (search) {
      const urlParams = new URLSearchParams(search);
      return [urlParams.get('review_lesson_id') || undefined, urlParams.get('study_plan_id') || undefined];
    } else {
      return [undefined, undefined];
    }
  }, [search]);
  const [, logout] = useLoginMutation(!!isParentAppCheck);
  const { boot, show, isOpen } = useIntercom();
  // isMobile
  const isMobile = useMediaQuery({maxWidth: mobileWidth});
  const [isMenuOpen, toggleMenu] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const { lockAccount, setLockAccount, parentFirstLogin, removeParentFirstLogin } = useAuthToken();
  const [heaUser] = useHeaUser()
  const history = useHistory();
  // const [heaLessonsToConfirm, {loading, data, refetch}] = useHeaLessonsToConfirmLazyQuery({ fetchPolicy: 'network-only' })

  // useEffect(() => {
  //   heaLessonsToConfirm() 
  // },[heaLessonsToConfirm])

  // useEffect(() => {
  //   if(refresh) {
  //     refetch()
  //   }
  // }, [refresh, refetch])
  useEffect(() => {
    if (parentFirstLogin && confirmLessons?.length) {
      setShowCompletedModal(true);
      removeParentFirstLogin();
    }
  }, [confirmLessons, parentFirstLogin, removeParentFirstLogin])
  const completedBadge = useMemo(() => {
    return confirmLessons?.length ?? 0
  },[confirmLessons])

  const VertDivider = () => <div className={styles.heaheaderDivider}/>;

  const onToggleMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu((state) => !state);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? "hidden" : "auto";
    document.getElementById("root")!.style.overflow = isMenuOpen ? "hidden" : "auto";
  }, [isMenuOpen])

  useEffect(() => {
    if (lockAccount === 'lock' && heaUser?.locked) {
      history.push("/locked");
    }

    const handleTabClose = (e: { preventDefault: () => void; returnValue: string; }) => {
      const event = e || window.event;
      if (event) {
        event.preventDefault();
        // if (lockAccount === 'lock' && heaUser?.locked) {
        //   event.returnValue = 'Do you really want to close tab?'
        setLockAccount('lock')
        //   return 'Do you really want to close tab?';
        // }
      }
      return;
    };

    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [setLockAccount, lockAccount, history, heaUser]);

  useEffect(() => {
    if (!!reviewLessonId && studyPlanId && studyPlans?.length && heaUnits?.length) {
      setShowCompletedModal(true);
    }
  }, [heaUnits?.length, reviewLessonId, studyPlanId, studyPlans?.length]);

  const onLiveChat = useCallback(() => {
    if (!isOpen) {
      boot({
        customAttributes: {
          horizontal_padding: 25,
        },
        hideDefaultLauncher: true,
      });
      show();
    }
  }, [boot, show, isOpen])

  const openCompletedLessonModal = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCompletedModal(true);
  }, []);

  const handleCloseConfirmModal = useCallback(() => {
    setShowCompletedModal(false);
    if (reviewLessonId || studyPlanId) {
      history.replace({ search: '' });
    }
    refetch?.();
  }, [history, refetch, reviewLessonId, studyPlanId]);
  const supportMenu = useMemo(() => ([
    // {
    //   title: 'Help Center',
    //   href: 'https://help.openparachuteschools.com/',
    // },
    {
      title: 'Contact Us',
      onClick: onLiveChat
    },
  ]), [onLiveChat]);

  return (
    <div className={isHidden ? styles.headerContainerHidden : styles.headerContainer}>
      <div className={`${styles.wrapper} ${isMenuOpen ? styles.openMenu : ''}`}>
        <div className={`${styles.darkOverlayHidden} ${styles.mobileOverlay}`}/>
        <div className={styles.logo}>
          <Logo />
        </div>
        {isMobile && (
          <div className={styles.togglerContainer}>
            <a onClick={onToggleMenu} className={styles.arrowMenuToggler} href="/">
              <ArrowDownIcon width={24} height={16}/>
            </a>
          </div>
        )}
        <div className={styles.links} style={{alignItems: isMobile ? "flex-start" : "center"}}>
          <>
            {/* <Link className={styles.support} href={'https://help.openparachuteschools.com/'}>{'SUPPORT'}</Link> */}
            {completedBadge > 0 && <div className={styles.confirmNotify}>
              <span>{'APPROVE COMPLETED LESSONS'}</span><ArrowRightIcon />
            </div>}
            <Link onClick={openCompletedLessonModal}>
              <div className={styles.initialContainer}>
                {loading ? <Spinner width={20} height={20} /> : <>
                  {completedBadge > 0 && <div className={styles.greenBadge}>{completedBadge}</div>}
                  <BellIcon />
                </>}
              </div>
            </Link>
            <VertDivider/>
            {!isHidden && <DropDown title={'SUPPORT'} list={supportMenu} />}
            <VertDivider/>
            <UserProfile mobileWidth={mobileWidth} menuText={menuText} isHidden={isHidden}/>
            {isMobile && <>
              <Link to={'/profile'}>{menuText.profile}</Link>
              <Link href='#' onClick={logout}>{menuText.logout}</Link>
            </>}
          </>
        </div>
      </div>
      {showCompletedModal && (
        <CompletedLessonsModal open={showCompletedModal} onClose={handleCloseConfirmModal} heaUnits={heaUnits} studyPlans={studyPlans} isParentApp={!!reviewLessonId} reviewLessonId={reviewLessonId} studyPlanId={studyPlanId} />
      )}
    </div>
  )
}

export default HeaHeader;
